import {
  convertFileToBase64,
  DynamicTabs,
  ModalDelete,
  PageWrapper,
  STATUSPAGE,
  UserTopCover,
} from '@repo/ui';
import { getEntityTypeLabel } from 'domain/value-objects/entity-type';
import { PROFILE_TYPE } from 'domain/value-objects/profile-type';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import logoOrganizacao from '../../../assets/images/users/logo-organizacao.jpg';
import { AdvertisingTabContent } from '../components/entity-settings/tabs-contents/advertising-tab-content';
import { CoursesTabContent } from '../components/entity-settings/tabs-contents/courses-tab-content';
import { FieldAssessments } from '../components/entity-settings/tabs-contents/field-assessments-tab-content';
import { NomenclaturesTabContent } from '../components/entity-settings/tabs-contents/nomenclatures-tab-content';
import { Overview } from '../components/entity-settings/tabs-contents/overview-tab-content';
import { PersonsTabContent } from '../components/entity-settings/tabs-contents/persons-tab-content';
import { UpdateOrganizationData } from '../components/entity-settings/tabs-contents/registration-data-tab-content';
import { SemestersTabContent } from '../components/entity-settings/tabs-contents/semesters-tab-content';
import { KolbDistributionTabContent } from '../components/entity-settings/tabs-contents/kolb-distribution-tab-content';
import { SettingOrganizationTab } from '../components/entity-settings/tabs-contents/setting-tab-content';
import { TcesTabContent } from '../components/entity-settings/tabs-contents/tces-tab-content';
import { useEntitySettings } from '../hooks/use-entity-settings';
import { useEntities } from '../hooks/useEntities';
import {
  EntitySettingsTabIds,
  validTabIds,
} from '../models/entity-settings-tabs';
import { getEnv } from 'shared/get-env';

export const Organization = () => {
  const { entityId } = useParams();

  const { activeTabId, setActiveTab } = useEntitySettings();

  const controller = useEntities();
  const {
    loadingStates,
    isModalPhotoOpen,
    isModalBannerOpen,
    responseEntitiesById,
    handleToggleModal,
    handleDeletePhoto,
    handleDeleteBanner,
    requestUploadPhoto,
    requestUploadBanner,
    requestEntitiesGetById,
    handleToggleModalPhoto,
    requestEntitiesSettingGetById,
  } = controller;

  const tabs = [
    {
      id: 'visao-geral',
      label: 'Visão Geral',
      tabContent: <Overview entityId={entityId} />,
    },
    {
      id: 'dados-cadastrais',
      label: 'Dados Cadastrais',
      tabContent: <UpdateOrganizationData controller={controller} />,
    },
    {
      id: 'configuracoes',
      label: 'Configurações',
      tabContent: <SettingOrganizationTab controller={controller} />,
    },
    {
      id: 'advertising',
      label: 'Publicidade',
      tabContent: <AdvertisingTabContent />,
    },
    {
      id: 'cursos',
      label: 'Cursos',
      tabContent: <CoursesTabContent entityId={entityId} />,
    },
    {
      id: 'semestres',
      label: 'Semestres',
      tabContent: <SemestersTabContent entityId={entityId} />,
    },
    {
      id: 'kolb-agrupamento',
      label: 'Agrupamento Kolb',
      tabContent: <KolbDistributionTabContent entityId={entityId} />,
    },
    {
      id: 'nomenclaturas',
      label: 'Nomenclaturas',
      tabContent: <NomenclaturesTabContent entityId={entityId} />,
    },
    {
      id: 'avaliacao-campo',
      label: 'Avaliação de Campo',
      tabContent: <FieldAssessments controller={controller} />,
    },
    ...(responseEntitiesById?.hasTces
      ? [
          {
            id: 'tce',
            label: 'TCE',
            tabContent: <TcesTabContent entityId={entityId} />,
          },
        ]
      : []),
    {
      id: 'equipe',
      label: 'Equipe',
      type: 'dropdown' as any,
      items: [
        {
          id: 'gestor',
          label: 'Gestor(a)',
          tabContent: (
            <PersonsTabContent
              entityId={entityId}
              profileType={PROFILE_TYPE.DIRECTOR}
            />
          ),
        },
        {
          id: 'coordenador',
          label: 'Coordenador(a)',
          icon: <i className="ri-group-line"></i>,
          tabContent: (
            <PersonsTabContent
              entityId={entityId}
              profileType={PROFILE_TYPE.COORDINATOR}
            />
          ),
        },
        {
          id: 'professor',
          label: 'Professor(a)',
          icon: <i className="ri-group-line"></i>,
          tabContent: (
            <PersonsTabContent
              entityId={entityId}
              profileType={PROFILE_TYPE.TEACHER}
            />
          ),
        },
        {
          id: 'preceptor',
          label: 'Preceptor(a)',
          icon: <i className="ri-group-line"></i>,
          tabContent: (
            <PersonsTabContent
              entityId={entityId}
              profileType={PROFILE_TYPE.PRECEPTOR}
            />
          ),
        },
        {
          id: 'assistente',
          label: 'Assistente',
          icon: <i className="ri-group-line"></i>,
          tabContent: (
            <PersonsTabContent
              entityId={entityId}
              profileType={PROFILE_TYPE.ASSISTANT}
            />
          ),
        },
        {
          id: 'auxiliar',
          label: 'Auxiliar',
          icon: <i className="ri-group-line"></i>,
          tabContent: (
            <PersonsTabContent
              entityId={entityId}
              profileType={PROFILE_TYPE.ADMINISTRATIVE}
            />
          ),
        },
      ],
    },
  ];

  useEffect(() => {
    if (!activeTabId || !validTabIds.includes(activeTabId)) {
      setActiveTab('visao-geral');
    }
  }, [activeTabId]);

  useEffect(() => {
    requestEntitiesGetById({ id: entityId });
    requestEntitiesSettingGetById({ id: entityId });
  }, []);

  return (
    <PageWrapper title="Organização">
      <ModalDelete
        loading={loadingStates.deleteBanner === STATUSPAGE.LOADING}
        isOpen={isModalBannerOpen}
        toggle={handleToggleModal}
        handleDelete={handleDeleteBanner}
      />
      <ModalDelete
        loading={loadingStates.deletePhoto === STATUSPAGE.LOADING}
        isOpen={isModalPhotoOpen}
        toggle={handleToggleModalPhoto}
        handleDelete={handleDeletePhoto}
      />
      <UserTopCover
        onDeleteAvatar={() => {
          handleToggleModalPhoto();
        }}
        onDeleteBanner={() => {
          handleToggleModal();
        }}
        canChangeAvatar
        isLoading={
          loadingStates.deletePhoto ||
          loadingStates.uploadPhoto ||
          loadingStates.getByIdEntities
            ? true
            : false
        }
        isLoadingChangeBanner={loadingStates.uploadBanner ? true : false}
        canChangeBanner
        onChangeBanner={(e) => {
          const { files } = e.target;
          if (files) {
            convertFileToBase64(files[0]).then((base64) => {
              requestUploadBanner({ banner: base64, id: entityId });
            });
          }
        }}
        handleFileChange={(e) => {
          const { files } = e.target;
          if (files) {
            convertFileToBase64(files[0]).then((base64) => {
              requestUploadPhoto({ photo: base64, id: entityId });
            });
          }
        }}
        expandableAvatar
        cover={
          responseEntitiesById?.banner ||
          getEnv('REACT_APP_ORGANIZATION_COVER_URL') ||
          ''
        }
        name={responseEntitiesById?.tradeName}
        iconLabels={[
          { icon: 'map-pin', label: responseEntitiesById?.address?.city?.name },
          {
            icon: 'tag',
            label: getEntityTypeLabel(responseEntitiesById?.type),
          },
        ]}
        avatar={
          responseEntitiesById?.photo
            ? responseEntitiesById?.photo
            : logoOrganizacao
        }
      />

      <DynamicTabs
        tabs={tabs}
        variant="pills"
        defaultActiveTabId={activeTabId}
        onTabChange={(tabId: EntitySettingsTabIds) => setActiveTab(tabId)}
        navClassName="animation-nav profile-nav gap-2 gap-lg-3 flex-grow-1"
        rerenderOnClickActiveTab={true}
      />
    </PageWrapper>
  );
};
